import {
  ApiError,
  apiDownload,
  apiRequest,
  apiRequestWithEtag,
  type IDownload,
  type IDownloadOptions,
  type IEtagged
} from '@/domains/api';
import { isObject, segment } from './rankings-api';
import type {
  IExportPreset,
  IExportPresetBody,
  IListAggregates,
  IListAggregatesRequest,
  IListAssignee,
  IListCounts,
  IListCountsRequest,
  IListEditResult,
  IListExportRequest,
  IListFacets,
  IListFacetsRequest,
  IListFieldCatalogue,
  IListFieldHiddenRule,
  IListFieldThreshold,
  IListFieldUsages,
  IListImport,
  IListImportList,
  IListImportRowList,
  IListImportUpload,
  IListRows,
  IListRowsRequest,
  IReinstatementRequest,
  IReinstatementResult,
  IReportRun,
  ISavedView,
  ISavedViewBody,
  ISavedViewSummary,
  ListEditRequest,
  ListImportRowFilter,
  ListImportState,
  SavedViewKind
} from './lists-contracts';

/**
 * survey-api's list engine (SR-230, merge request !97) — **the one reader** of every route the
 * Lists, Reports, Import and Export layouts screens call: the field catalogue and a threshold's
 * write, the rows, facets, aggregates and assignees, the saved views, the cell edits, the export,
 * the export presets, the report runs and their files, the imports with their rows, apply,
 * reversal and result file. Design: `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §1 and §9. Nothing here filters, counts, sums or judges a value: survey-api's 400 `invalid-body`
 * names every problem by its path, and the screen places it.
 *
 * **A revision is an `ETag`, quoted back as read** — the work items' rule. A view's and a preset's
 * read, create and save hand it back (`apiRequestWithEtag`); a save and a delete quote it as
 * `If-Match` (428 without it, 409 `revision-conflict` on a stale one). It is never parsed into the
 * body's `revision`.
 *
 * **An id is a GUID, and anything else is refused before a request** (`isListId`, the refresh
 * run's `isRefreshRunId` precedent): a view's, a run's or an import's id comes from the address
 * bar and is spliced into a path, where `views/.` would reach the list. A read answers `null`
 * unsent, as for a 404; a write throws, since its id came from survey-api and anything else is a
 * bug here. A field code in a path is sent as one segment (`segment`), dot segments refused.
 *
 * **A 2xx without the member a screen draws is a failed read** (`ranking-process-api.ts`' rule):
 * the screens hold the answer over their tables, and this app has no error boundary. Each reader
 * checks the members its screen draws first and throws "survey-api answered with no … in it".
 *
 * **The upload is the one multipart body this app sends**: a `FormData` the client passes on as it
 * is, the browser writing the boundary (`client.ts`). The export and the two files are
 * `apiDownload`'s, named by `Content-Disposition`.
 */

const ADMIN = '/api/v1/admin';
const LISTS = `${ADMIN}/lists`;
const VIEWS = `${ADMIN}/views`;
const PRESETS = `${ADMIN}/export-presets`;
const RUNS = `${ADMIN}/report-runs`;
const IMPORTS = `${ADMIN}/imports`;

/** survey-api's ids for views, presets, runs and imports — `{…:guid}` on every route taking one. */
const LIST_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a value can name a view, a preset, a run or an import — asked before any request. */
export function isListId(value: string): boolean {
  return LIST_ID.test(value);
}

/** A write's id came from survey-api; one that is no GUID is this app's bug, thrown unsent. */
function sentId(id: string, what: string): string {
  if (!isListId(id)) throw new Error(`"${id}" is not ${what}'s id.`);
  return id;
}

function query(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(name, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

function refused(what: string): Error {
  return new Error(`The answer held no ${what}`);
}

/** The answer, once each named member is an array — else a failed read. */
function withArrays<T>(answer: unknown, members: readonly string[], what: string): T {
  if (!isObject(answer) || members.some((name) => !Array.isArray(answer[name]))) {
    throw refused(what);
  }
  return answer as T;
}

/** The answer, once each named member is a number — else a failed read. */
function withNumbers(answer: unknown, members: readonly string[], what: string): void {
  if (!isObject(answer) || members.some((name) => typeof answer[name] !== 'number')) {
    throw refused(what);
  }
}

function viewOf(answer: IEtagged<unknown>): IEtagged<ISavedView> {
  const { body } = answer;
  if (!isObject(body) || typeof body.id !== 'string' || !isObject(body.query)) {
    throw refused('view');
  }
  withArrays(body, ['formattingRules'], 'view');
  return answer as IEtagged<ISavedView>;
}

function presetOf(answer: IEtagged<unknown>): IEtagged<IExportPreset> {
  const { body } = answer;
  if (!isObject(body) || typeof body.id !== 'string') throw refused('export layout');
  withArrays(body, ['columns'], 'export layout');
  return answer as IEtagged<IExportPreset>;
}

/** What an export's caller may be told while the file streams. */
export interface IExportWatch extends Pick<IDownloadOptions, 'signal' | 'onProgress'> {
  /** At the first byte: survey-api's name for the file, and the rows it will hold — null where a
   *  header did not come or is not exposed to this origin. */
  onStart?: (start: { fileName: Nullable<string>; rows: Nullable<number> }) => void;
}

/** A count header's value, or null for none or one that is not a count. */
function countOf(value: Nullable<string>): Nullable<number> {
  const count = value === null || value.trim() === '' ? NaN : Number(value);
  return Number.isSafeInteger(count) && count >= 0 ? count : null;
}

function runOf(answer: unknown): IReportRun {
  if (!isObject(answer) || typeof answer.id !== 'string' || typeof answer.state !== 'string') {
    throw refused('report run');
  }
  return answer as unknown as IReportRun;
}

function importOf(answer: unknown): IListImport {
  if (!isObject(answer) || typeof answer.id !== 'string' || typeof answer.state !== 'string') {
    throw refused('import');
  }
  withArrays(answer, ['columns'], 'import');
  if (!isObject(answer.counts)) throw refused('import');
  return answer as unknown as IListImport;
}

/** A read that answers `null` for a 404 — a view, run or import the caller cannot open, or an
 *  unknown survey. Every other refusal still throws. */
async function orNull<T>(read: Promise<T>): Promise<Nullable<T>> {
  try {
    return await read;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

function ifMatch(etag: string): Record<string, string> {
  return { 'If-Match': etag };
}

export const listsApi = {
  /** A survey's field catalogue, or `all`'s cross-survey one, as the caller may see it; `null`
   *  for a survey this deployment does not hold (a bodiless 404 — `local` has no Canada). */
  fields(survey: string, signal?: AbortSignal, surveys?: readonly string[]): Promise<Nullable<IListFieldCatalogue>> {
    // SR-499: `all` joined with several surveys' own catalogues, one `surveys` key each.
    const joined = (surveys ?? []).map((code) => `&surveys=${encodeURIComponent(code)}`).join('');
    return orNull(
      apiRequest<unknown>(`${ADMIN}/list-fields${query({ survey })}${joined}`, { signal }).then((answer) =>
        withArrays<IListFieldCatalogue>(answer, ['groups'], 'field catalogue')
      )
    );
  },

  /** A page under the query, or with `since` and `known` a poll (`rows` changed, `left`,
   *  `entered`). 400 `invalid-body` names every problem under `query.`. */
  async rows(body: IListRowsRequest, signal?: AbortSignal): Promise<IListRows> {
    const answer = await apiRequest<unknown>(`${LISTS}/rows`, { method: 'POST', body, signal });
    withNumbers(answer, ['total'], 'rows');
    return withArrays<IListRows>(answer, ['rows'], 'rows');
  },

  /** A funnel's values with their counts, blanks as `value: null`, `truncated` past 500. */
  async facets(body: IListFacetsRequest, signal?: AbortSignal): Promise<IListFacets> {
    const answer = await apiRequest<unknown>(`${LISTS}/facets`, { method: 'POST', body, signal });
    return withArrays<IListFacets>(answer, ['values'], 'values');
  },

  /** The result's count and Σ, and every `groupBy` level's headings. */
  async aggregates(body: IListAggregatesRequest, signal?: AbortSignal): Promise<IListAggregates> {
    const answer = await apiRequest<unknown>(`${LISTS}/aggregates`, {
      method: 'POST',
      body,
      signal
    });
    withNumbers(answer, ['count'], 'totals');
    if (!isObject(answer) || !isObject(answer.sums)) throw refused('totals');
    return withArrays<IListAggregates>(answer, ['groups'], 'totals');
  },

  /** Each key's count under the base query AND its criteria — a page's cards and their signals
   *  in one read. */
  async counts(body: IListCountsRequest, signal?: AbortSignal): Promise<IListCounts> {
    const answer = await apiRequest<unknown>(`${LISTS}/counts`, { method: 'POST', body, signal });
    if (!isObject(answer) || !isObject(answer.counts)) throw refused('counts');
    return answer as unknown as IListCounts;
  },

  /** The accounts the *Assigned to* picker offers, by email. */
  async assignees(signal?: AbortSignal): Promise<IListAssignee[]> {
    const answer = await apiRequest<unknown>(`${LISTS}/assignees`, { signal });
    return withArrays<{ assignees: IListAssignee[] }>(answer, ['assignees'], 'assignees').assignees;
  },

  /** The saved views the caller may open, of a survey (`all` the cross-survey ones) and a kind;
   *  `null` for an unknown survey (404). */
  listViews(
    survey: string,
    kind?: SavedViewKind,
    signal?: AbortSignal
  ): Promise<Nullable<ISavedViewSummary[]>> {
    return orNull(
      apiRequest<unknown>(`${VIEWS}${query({ survey, kind })}`, { signal }).then(
        (answer) =>
          withArrays<{ views: ISavedViewSummary[] }>(answer, ['views'], 'saved views').views
      )
    );
  },

  /** One view with its `ETag` — the read stamps the caller's open. `null` for a view the caller
   *  cannot open (404) or an id that is no GUID, answered unsent. */
  openView(viewId: string, signal?: AbortSignal): Promise<Nullable<IEtagged<ISavedView>>> {
    if (!isListId(viewId)) return Promise.resolve(null);
    return orNull(
      apiRequestWithEtag<unknown>(`${VIEWS}/${viewId}`, { signal }).then(viewOf)
    );
  },

  /** One view with its `ETag`, read without stamping the caller's open (SR-412) — the picker's
   *  dialog reads a view's revision to rename it, make it the default or delete it. */
  peekView(viewId: string, signal?: AbortSignal): Promise<Nullable<IEtagged<ISavedView>>> {
    if (!isListId(viewId)) return Promise.resolve(null);
    return orNull(
      apiRequestWithEtag<unknown>(`${VIEWS}/${viewId}?peek=true`, { signal }).then(viewOf)
    );
  },

  /** A new view, 201 with its `ETag`. 403 bodiless for a visibility or a default the caller may
   *  not give (`lists.share`); 400 by path. */
  async createView(body: ISavedViewBody): Promise<IEtagged<ISavedView>> {
    return viewOf(await apiRequestWithEtag<unknown>(VIEWS, { method: 'POST', body }));
  },

  /** The whole view under `If-Match`; the answer is the view as saved with its next `ETag`. 409
   *  `revision-conflict` (`currentRevision`), 403 for a view the caller may not save over. */
  async saveView(viewId: string, etag: string, body: ISavedViewBody): Promise<IEtagged<ISavedView>> {
    const id = sentId(viewId, 'a view');
    return viewOf(
      await apiRequestWithEtag<unknown>(`${VIEWS}/${id}`, {
        method: 'PUT',
        body,
        headers: ifMatch(etag)
      })
    );
  },

  /** A view's delete under `If-Match`, 204. 409 `seeded-view` for a seed, `revision-conflict`. */
  async deleteView(viewId: string, etag: string): Promise<void> {
    const id = sentId(viewId, 'a view');
    await apiRequest<null>(`${VIEWS}/${id}`, { method: 'DELETE', headers: ifMatch(etag) });
  },

  /** Save data, a selection's set and assign, or a mass update — one audited batch, every cell
   *  answered `applied`, `unchanged` or in `conflicts`. 403 bodiless for a cell the caller may not
   *  write (nothing written), 400 by path (`cells[3].value`). */
  async edit(body: ListEditRequest): Promise<IListEditResult> {
    const answer = await apiRequest<unknown>(`${LISTS}/edits`, { method: 'POST', body });
    if (!isObject(answer) || typeof answer.batchId !== 'string') throw refused('edit');
    return withArrays<IListEditResult>(answer, ['applied', 'unchanged', 'conflicts'], 'edit');
  },

  /** The reinstatement worklist's decision over rows of one survey — one audited batch; a row whose
   *  Quality moved on is a conflict, one no longer on the worklist is skipped. 400 by path. */
  async reinstate(body: IReinstatementRequest): Promise<IReinstatementResult> {
    const answer = await apiRequest<unknown>(`${ADMIN}/reinstatements`, { method: 'POST', body });
    if (!isObject(answer) || typeof answer.batchId !== 'string') throw refused('reinstatement');
    return withArrays<IReinstatementResult>(answer, ['applied', 'conflicts', 'skipped'], 'reinstatement');
  },

  /** The rows under the query as a file, streamed and named by survey-api. 400 at `format` or
   *  `presetId`. Its `X-Total-Count`, the rows the file holds, comes with the first byte
   *  (`onStart`); the body has no length, so the bytes received are all `onProgress` can say. */
  exportRows(body: IListExportRequest, watch: IExportWatch = {}): Promise<IDownload> {
    const { signal, onStart, onProgress } = watch;
    return apiDownload(`${LISTS}/export`, {
      method: 'POST',
      body,
      signal,
      onProgress,
      onStart:
        onStart &&
        (({ fileName, headers }) =>
          onStart({ fileName, rows: countOf(headers.get('X-Total-Count')) }))
    });
  },

  /** The export layouts for a survey (and a firm); `null` for an unknown survey (404). */
  presets(
    filter: { survey?: string; firm?: string } = {},
    signal?: AbortSignal
  ): Promise<Nullable<IExportPreset[]>> {
    return orNull(
      apiRequest<unknown>(`${PRESETS}${query(filter)}`, { signal }).then(
        (answer) =>
          withArrays<{ presets: IExportPreset[] }>(answer, ['presets'], 'export layouts').presets
      )
    );
  },

  /** One layout with its `ETag`; `null` for no such layout or an id that is no GUID. */
  preset(presetId: string, signal?: AbortSignal): Promise<Nullable<IEtagged<IExportPreset>>> {
    if (!isListId(presetId)) return Promise.resolve(null);
    return orNull(
      apiRequestWithEtag<unknown>(`${PRESETS}/${presetId}`, { signal }).then(presetOf)
    );
  },

  /** A new layout (`firm_exchange.manage`), 201 with its `ETag`; 400 by path. */
  async createPreset(body: IExportPresetBody): Promise<IEtagged<IExportPreset>> {
    return presetOf(await apiRequestWithEtag<unknown>(PRESETS, { method: 'POST', body }));
  },

  /** The whole layout under `If-Match`; 409 `revision-conflict`. */
  async savePreset(
    presetId: string,
    etag: string,
    body: IExportPresetBody
  ): Promise<IEtagged<IExportPreset>> {
    const id = sentId(presetId, 'an export layout');
    return presetOf(
      await apiRequestWithEtag<unknown>(`${PRESETS}/${id}`, {
        method: 'PUT',
        body,
        headers: ifMatch(etag)
      })
    );
  },

  /** A layout's delete under `If-Match`, 204; 409 `seeded-preset` for a seed. */
  async deletePreset(presetId: string, etag: string): Promise<void> {
    const id = sentId(presetId, 'an export layout');
    await apiRequest<null>(`${PRESETS}/${id}`, { method: 'DELETE', headers: ifMatch(etag) });
  },

  /** A report's run, 202 `queued` — the Worker writes the file. 409 `not-a-report` for a list. */
  async requestRun(viewId: string, note?: string): Promise<IReportRun> {
    const id = sentId(viewId, 'a view');
    return runOf(
      await apiRequest<unknown>(`${VIEWS}/${id}/runs`, {
        method: 'POST',
        body: note ? { note } : null
      })
    );
  },

  /** A report's runs, newest first; `null` for a view the caller cannot open. */
  runs(viewId: string, signal?: AbortSignal): Promise<Nullable<IReportRun[]>> {
    if (!isListId(viewId)) return Promise.resolve(null);
    return orNull(
      apiRequest<unknown>(`${VIEWS}/${viewId}/runs`, { signal }).then(
        (answer) => withArrays<{ runs: IReportRun[] }>(answer, ['runs'], 'runs').runs
      )
    );
  },

  /** One run; `null` for no such run or an id that is no GUID. */
  run(runId: string, signal?: AbortSignal): Promise<Nullable<IReportRun>> {
    if (!isListId(runId)) return Promise.resolve(null);
    return orNull(apiRequest<unknown>(`${RUNS}/${runId}`, { signal }).then(runOf));
  },

  /** A finished run's file. 403 bodiless on a restricted run for a caller without
   *  `internal_fields.restricted`. */
  runFile(runId: string): Promise<IDownload> {
    return apiDownload(`${RUNS}/${sentId(runId, 'a report run')}/file`);
  },

  /** A file to check, as a multipart form: 202 with the import `queued` — the Worker checks it.
   *  413 `import-too-large`, 400 by field (`file`, `survey`, `presetId`). */
  async upload({ file, survey, note, presetId }: IListImportUpload): Promise<IListImport> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('survey', survey);
    if (note) form.append('note', note);
    if (presetId) form.append('presetId', presetId);
    return importOf(await apiRequest<unknown>(IMPORTS, { method: 'POST', body: form }));
  },

  /** A page of imports, newest first; `q` words each in the file name or the importer's email,
   *  `pageSize` 1–100 (survey-api's 50 without one); 400 `invalid-query` naming the parameter. */
  async imports(
    filter: { survey?: string; state?: ListImportState; q?: string; page?: number; pageSize?: number },
    signal?: AbortSignal
  ): Promise<IListImportList> {
    const answer = await apiRequest<unknown>(`${IMPORTS}${query(filter)}`, { signal });
    withNumbers(answer, ['total'], 'imports');
    return withArrays<IListImportList>(answer, ['imports'], 'imports');
  },

  /** One import; `null` for no such import or an id that is no GUID. */
  import(importId: string, signal?: AbortSignal): Promise<Nullable<IListImport>> {
    if (!isListId(importId)) return Promise.resolve(null);
    return orNull(apiRequest<unknown>(`${IMPORTS}/${importId}`, { signal }).then(importOf));
  },

  /** A page of an import's rows under a tab; 403 on a restricted import without
   *  `internal_fields.restricted`; `null` for no such import. */
  importRows(
    importId: string,
    filter: { outcome?: ListImportRowFilter; page?: number },
    signal?: AbortSignal
  ): Promise<Nullable<IListImportRowList>> {
    if (!isListId(importId)) return Promise.resolve(null);
    return orNull(
      apiRequest<unknown>(`${IMPORTS}/${importId}/rows${query(filter)}`, { signal }).then(
        (answer) => {
          withNumbers(answer, ['total'], 'rows');
          return withArrays<IListImportRowList>(answer, ['rows'], 'rows');
        }
      )
    );
  },

  /** Apply a checked import, 202 `committing`; `includeHeld` the row numbers whose held changes
   *  are written too. 409 `import-state`. */
  async commit(importId: string, includeHeld: number[] = []): Promise<IListImport> {
    const id = sentId(importId, 'an import');
    return importOf(
      await apiRequest<unknown>(`${IMPORTS}/${id}/commit`, {
        method: 'POST',
        body: includeHeld.length > 0 ? { includeHeld } : null
      })
    );
  },

  /** Reverse an applied import, 202 `reversing`; the note is required (400 at `note`). 409
   *  `import-state`. */
  async reverse(importId: string, note: string): Promise<IListImport> {
    const id = sentId(importId, 'an import');
    return importOf(
      await apiRequest<unknown>(`${IMPORTS}/${id}/reversal`, { method: 'POST', body: { note } })
    );
  },

  /** The import's result file — the rows as checked and what became of each. */
  resultFile(importId: string): Promise<IDownload> {
    return apiDownload(`${IMPORTS}/${sentId(importId, 'an import')}/result`);
  },

  /** A field's anomaly threshold on a survey (`internal_fields.author`), a fraction; `null`
   *  clears it. 400 at `threshold`, 404 for an unknown survey or field. */
  async setThreshold(
    survey: string,
    field: string,
    threshold: Nullable<number>
  ): Promise<IListFieldThreshold> {
    const answer = await apiRequest<unknown>(
      `${ADMIN}/list-fields/${segment(field)}/anomaly-threshold${query({ survey })}`,
      { method: 'PUT', body: { threshold } }
    );
    if (!isObject(answer) || typeof answer.field !== 'string') throw refused('threshold');
    return answer as unknown as IListFieldThreshold;
  },

  /** The fields and origins hidden from the pickers in every survey (SR-462, `internal_fields.author`). */
  async hiddenFields(signal?: AbortSignal): Promise<IListFieldHiddenRule[]> {
    const answer = await apiRequest<unknown>(`${ADMIN}/list-fields/hidden`, { signal });
    return withArrays<{ rules: IListFieldHiddenRule[] }>(answer, ['rules'], 'hidden fields').rules;
  },

  /** Which saved views and email templates use which fields (SR-462, `internal_fields.author`). */
  async fieldUsage(signal?: AbortSignal): Promise<IListFieldUsages> {
    const answer = await apiRequest<unknown>(`${ADMIN}/list-fields/usage`, { signal });
    return withArrays<IListFieldUsages>(answer, ['views', 'fields'], 'field usage');
  },

  /** Hides a field code or an origin from the pickers, or shows it again. 400 at `kind` or `key`. */
  async setHidden(kind: IListFieldHiddenRule['kind'], key: string, hidden: boolean): Promise<void> {
    await apiRequest<unknown>(`${ADMIN}/list-fields/hidden/${segment(kind)}/${segment(key)}`, {
      method: hidden ? 'PUT' : 'DELETE'
    });
  }
};
